import { inject, Injectable } from '@angular/core';
import { Dtc } from '../../../../../shared/models/dtc/dtc';
import { ErrorMessage } from '../../../../../shared/models/error-message/error-message';
import { ErrorMessageApiService } from '../../../../services/api/error-message-api.service';
import { DtcApiService } from '../../../../services/api/dtc-api.service';
import { AuditLog } from '../../../../../shared/models/audit/audit-log';
import { AuditChange } from '../../../../../shared/models/audit/audit-change';
import { ErrorMessageQuery } from '../../../../../shared/models/error-messages/error-message-query';
import { firstValueFrom } from 'rxjs';
import { DtcQuery } from '../../../../../shared/models/dtc/dtc-query';
import { AUDIT_CHANGE_FIELD_NAMES } from '../../../../../shared/constants/audit-change-field-names';
import { PreselectionType } from '../../../../../shared/enums/preselection-type';
import { SwPartNumber } from '../../../../../shared/models/sw-part-number/sw-part-number';
import { SwPartNumberApiService } from '../../../../services/api/sw-part-number-api.service';
import { SwPartNumberQuery } from '../../../../../shared/models/sw-part-number/sw-part-number-query';
import { errorToString } from '../../../../common/common-utils';

@Injectable()
export class AuditChangeDecorator {
  private errorMessages = new Map<string, ErrorMessage | null>();
  private dtcs = new Map<string, Dtc | null>();
  private swPartNumbers = new Map<string, SwPartNumber | null>();
  private auditLog: AuditLog[] = [];

  private errorMessageApi = inject(ErrorMessageApiService);
  private dtcApi = inject(DtcApiService);
  private swPartNumberApi = inject(SwPartNumberApiService);

  public async decorateChanges(auditLog: AuditLog[], handler: (decoratedLogs: AuditLog[]) => void): Promise<void> {
    this.auditLog = auditLog;

    this.scanForEntityIds();
    await this.loadErrorMessages();
    await this.loadDtcs();
    await this.loadSwPartNumbers();
    this.rewriteChanges();
    handler(this.auditLog);
  }

  private scanForEntityIds(): void {
    for (const audit of this.auditLog!) {
      try {
        if (audit.changedProperties == null || audit.changedProperties == '') {
          audit.changes = undefined;
          continue;
        }
        const rawChanges = JSON.parse(audit.changedProperties ?? '');
        this.scanChanges(rawChanges);
        audit.changes = rawChanges;
      } catch (e) {
        console.error(errorToString(e));
      }
    }
  }

  private scanChanges(changes: AuditChange[]): void {
    if (changes == null) {
      return;
    }
    for (const change of changes!) {
      let list = this.errorMessages;
      if (
        change.fieldName == AUDIT_CHANGE_FIELD_NAMES.DTC_ID ||
        change.fieldName == AUDIT_CHANGE_FIELD_NAMES.DTC_CHILD_ID ||
        change.fieldName == AUDIT_CHANGE_FIELD_NAMES.DTC_PARENT_ID
      ) {
        list = this.dtcs;
      }

      if (
        change.fieldName == AUDIT_CHANGE_FIELD_NAMES.SW_PART_NUMBER_CHILD_ID ||
        change.fieldName == AUDIT_CHANGE_FIELD_NAMES.SW_PART_NUMBER_PARENT_ID
      ) {
        list = this.swPartNumbers;
      }

      if (change.newVal?.length ?? 0 > 0) {
        list.set(change.newVal!, null);
      }
      if (change.oldVal?.length ?? 0 > 0) {
        list.set(change.oldVal!, null);
      }
    }
  }

  private async loadErrorMessages(): Promise<void> {
    let morePages = true;
    let page = 1;

    do {
      const query: ErrorMessageQuery = {
        ids: Array.from(this.errorMessages.keys()).join(','),
        page: page,
      };

      if (query.ids?.length == 0) {
        return;
      }

      const errorMessages = await firstValueFrom(this.errorMessageApi.getErrorMessages(query));
      for (const em of errorMessages.items) {
        if (em.id) {
          this.errorMessages.set(em.id, em);
        }
      }

      morePages = errorMessages.currentPage < errorMessages.totalPages;
      page++;
    } while (morePages);
  }

  private async loadDtcs(): Promise<void> {
    let morePages = true;
    let page = 1;
    do {
      const query: DtcQuery = {
        ids: Array.from(this.dtcs.keys()).join(','),
        page: page,
      };

      if (query.ids?.length == 0) {
        return;
      }

      const dtcs = await firstValueFrom(this.dtcApi.getDtcs(query));
      for (const dtc of dtcs.items) {
        if (dtc.id) {
          this.dtcs.set(dtc.id, dtc);
        }
      }

      morePages = dtcs.currentPage < dtcs.totalPages;
      page++;
    } while (morePages);
  }

  private async loadSwPartNumbers(): Promise<void> {
    let morePages = true;
    let page = 1;
    do {
      const query: SwPartNumberQuery = {
        ids: Array.from(this.swPartNumbers.keys()).join(','),
        page: page,
      };

      if (query.ids?.length == 0) {
        return;
      }

      const swPartNumbers = await firstValueFrom(this.swPartNumberApi.searchSwPartNumbers(query));
      for (const swPartNumber of swPartNumbers.items) {
        if (swPartNumber.id) {
          this.swPartNumbers.set(swPartNumber.id, swPartNumber);
        }
      }

      morePages = swPartNumbers.currentPage < swPartNumbers.totalPages;
      page++;
    } while (morePages);
  }

  private rewriteChanges(): void {
    for (const audit of this.auditLog!) {
      for (const change of audit.changes ?? []) {
        change.newTooltip = '';
        change.oldTooltip = '';

        switch (change.fieldName) {
          case AUDIT_CHANGE_FIELD_NAMES.ERROR_MESSAGE_ID:
          case AUDIT_CHANGE_FIELD_NAMES.ERROR_MESSAGE_PARENT_ID:
          case AUDIT_CHANGE_FIELD_NAMES.ERROR_MESSAGE_CHILD_ID:
            this.decorateErrorMessageChange(change);
            break;
          case AUDIT_CHANGE_FIELD_NAMES.DTC_ID:
          case AUDIT_CHANGE_FIELD_NAMES.DTC_PARENT_ID:
          case AUDIT_CHANGE_FIELD_NAMES.DTC_CHILD_ID:
            this.decorateDtcChange(change);
            break;
          case AUDIT_CHANGE_FIELD_NAMES.SW_PART_NUMBER_CHILD_ID:
          case AUDIT_CHANGE_FIELD_NAMES.SW_PART_NUMBER_PARENT_ID:
            this.decorateSwPartNumberChange(change);
            break;
        }
      }
    }
  }
  private decorateErrorMessageChange(change: AuditChange): void {
    change.fieldName = 'Error Message' + this.fieldNameExtension(change.fieldName);
    change.entityType = PreselectionType.ErrorMessage;

    [change.newVal, change.newTooltip, change.entityIdNew] = this.formatErrorMessageForDisplay(change.newVal);
    [change.oldVal, change.oldTooltip, change.entityIdOld] = this.formatErrorMessageForDisplay(change.oldVal);
  }

  private decorateDtcChange(change: AuditChange): void {
    change.fieldName = 'DTC' + this.fieldNameExtension(change.fieldName);
    change.entityType = PreselectionType.Dtc;

    [change.newVal, change.newTooltip, change.entityIdNew] = this.formatDtcForDisplay(change.newVal);
    [change.oldVal, change.oldTooltip, change.entityIdOld] = this.formatDtcForDisplay(change.oldVal);
  }

  private decorateSwPartNumberChange(change: AuditChange): void {
    change.fieldName = 'SwPartNumber' + this.fieldNameExtension(change.fieldName);
    change.entityType = PreselectionType.SwPartNumber;

    [change.newVal, change.newTooltip, change.entityIdNew] = this.formatSwPartNumberForDisplay(change.newVal);
    [change.oldVal, change.oldTooltip, change.entityIdOld] = this.formatSwPartNumberForDisplay(change.oldVal);
  }

  private fieldNameExtension(originalFieldName: string): string {
    if (originalFieldName.endsWith('ParentId')) {
      return ' cloned from';
    }

    if (originalFieldName.endsWith('ChildId')) {
      return ' cloned to';
    }
    return '';
  }

  private formatErrorMessageForDisplay(id: string | undefined): [string, string, string] {
    const em = this.errorMessages.get(id ?? '');
    if (em) {
      return [
        `${em.code ? em.code + ' - ' : ''}${em.headerOnTerminal ?? ''}`,
        `${em.category ?? 'Unknown'}: ${em.detailsOnTerminal}`,
        id!,
      ];
    }

    if (id && id.length > 0) {
      return ['N/A', '(Error Message has been deleted)', ''];
    }
    return ['', '', ''];
  }

  private formatDtcForDisplay(id: string | undefined): [string, string, string] {
    const dtc = this.dtcs.get(id ?? '');

    if (dtc) {
      return [dtc.displayId ?? '', dtc.implementationHint ?? '', id!];
    }

    if (id && id.length > 0) {
      return ['N/A', '(Dtc has been deleted)', ''];
    }
    return ['', '', ''];
  }

  private formatSwPartNumberForDisplay(id: string | undefined): [string, string, string] {
    const spn = this.swPartNumbers.get(id ?? '');

    if (spn) {
      return [spn.displayId ?? '', '', id!];
    }

    if (id && id.length > 0) {
      return ['N/A', '(SwPartNumber has been deleted)', ''];
    }
    return [id ?? '', '', ''];
  }
}
